import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import useIsDesktop from './hooks/useIsDesktop';
import DesktopApp from './desktop/DesktopApp';
import DesktopLogin from './desktop/pages/Login';
import ProtectedRoute from './components/ProtectedRoute';
import ViewAsBanner from './components/ViewAsBanner';
import ViewAsPage from './pages/ViewAsPage';
import { viewAs } from './api/client';
import LoadBoundary from './components/events/LoadBoundary';
import AppShell from './components/AppShell';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PublicSigningPage from './pages/PublicSigningPage';
import ReloadPrompt from './components/ReloadPrompt';
import InstallPrompt from './components/InstallPrompt';
import PushPrompt from './components/PushPrompt';
import WhatsNew from './components/WhatsNew';
import MemoGate from './components/memos/MemoGate';
import HomePage from './pages/HomePage';
import SalesPage from './pages/SalesPage';
import SaleDetailPage from './pages/SaleDetailPage';
import BranchSalePage from './pages/BranchSalePage';
import SaleCreatePage from './pages/SaleCreatePage';
import ListingsPage from './pages/ListingsPage';
import ListingDetailPage from './pages/ListingDetailPage';
import ListingFormPage from './pages/ListingFormPage';
import CoSharePage from './pages/CoSharePage';
import CoShareDetailPage from './pages/CoShareDetailPage';
import CoShareFormPage from './pages/CoShareFormPage';
import EarningsPage from './pages/EarningsPage';
import TierLadderPage from './pages/TierLadderPage';
import StatementPreviewPage from './pages/StatementPreviewPage';
import MorePage from './pages/MorePage';
import FormsPage from './pages/FormsPage';
import FormDetailPage from './pages/FormDetailPage';
import FormCreatePage from './pages/FormCreatePage';
import LettersPage from './pages/LettersPage';
import LetterDetailPage from './pages/LetterDetailPage';
import LetterCreatePage from './pages/LetterCreatePage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import ContactFormPage from './pages/ContactFormPage';
import TeamPage from './pages/TeamPage';
import TeamHierarchyPage from './pages/TeamHierarchyPage';
import TeamPerformancePage from './pages/TeamPerformancePage';
import SimsPage from './pages/SimsPage';
import ToolsPage from './pages/ToolsPage';
import DownloadsPage from './pages/DownloadsPage';
import DocumentWorkspacePage from './pages/DocumentWorkspacePage';
import MortgagePage from './pages/MortgagePage';
import MortgageResultPage from './pages/MortgageResultPage';
import ComparePage from './pages/ComparePage';
import RefinancePage from './pages/RefinancePage';
import RefinanceResultPage from './pages/RefinanceResultPage';
import TenancyPage from './pages/TenancyPage';
import ValuationPage from './pages/ValuationPage';
import LadPage from './pages/LadPage';
import AffordabilityPage from './pages/AffordabilityPage';
import DsrPage from './pages/DsrPage';
import NotifsPage from './pages/NotifsPage';
import MemosPage from './pages/MemosPage';
import MemoDetailPage from './pages/MemoDetailPage';
import ProfilePage from './pages/ProfilePage';
// Events load on demand: the main chunk sits just under Workbox's 2 MiB precache
// limit, and the calendar, ticket QR and scanner are not first-screen material.
const EventsPage = lazy(() => import('./pages/EventsPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const EventTicketPage = lazy(() => import('./pages/EventTicketPage'));
const EventScanPage = lazy(() => import('./pages/EventScanPage'));
const EventCheckinLinkPage = lazy(() => import('./pages/EventCheckinLinkPage'));
// The public guest registration page (external guests, no login): its own chunk.
const JoinEventPage = lazy(() => import('./pages/JoinEventPage'));
// Release notes too: read once per update, and the main chunk has ~50 KB left.
const WhatsNewPage = lazy(() => import('./pages/WhatsNewPage'));
// Tutorials: how-it-works videos, by topic.
const TutorialsPage = lazy(() => import('./pages/TutorialsPage'));
const TutorialWatchPage = lazy(() => import('./pages/TutorialWatchPage'));
// Project materials (brochures, price lists, photos, videos HQ uploads per project).
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectMaterialsPage = lazy(() => import('./pages/ProjectMaterialsPage'));
// A chunk that fails to load (dropped connection, a deploy mid-session) shows a
// Reload screen instead of the router's developer page; keyed on the path so the
// next screen starts clean.
function OnDemand({ children }) {
  const { pathname } = useLocation();
  return (
    <LoadBoundary key={pathname}>
      <Suspense fallback={<div style={{ flex: 1 }} />}>{children}</Suspense>
    </LoadBoundary>
  );
}
import LearnPage from './pages/LearnPage';
import { TourProvider } from './learn/TourContext';
import TourOverlay from './learn/TourOverlay';
import { useTour } from './learn/TourContext';

// Full-screen (no bottom tab bar) group — detail/create + secondary lists reached from More.
function FullScreen() {
  // Keyed on the tour's remount counter so a simulated HQ beat re-reads its data.
  const { remountKey } = useTour() || {};
  return (
    <ProtectedRoute>
      <div className="app-frame">
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Outlet key={remountKey} />
        </div>
      </div>
    </ProtectedRoute>
  );
}

export default function App() {
  // ── Desktop split ──────────────────────────────────────────────────────────
  // At >=1100px the desktop shell takes over: the sidebar replaces the bottom
  // tab bar, lists become tables, sheets become drawers. Below it nothing
  // changes — this is the phone build, untouched.
  //
  // A separate component tree rather than a restyle: the two share their data
  // (src/api, src/hooks, src/context) but not their layout, and trying to make
  // one tree do both is how you get a screen that is bad at each.
  //
  // The public routes (login, reset, customer signing) stay common. Signing in
  // particular is a token-gated page a customer opens from an email on whatever
  // device they have, so it keeps its single responsive layout.
  const desktop = useIsDesktop();
  // HQ opened this tab from the admin portal's "View as agent" (read only).
  const viewing = viewAs.active;
  const { pathname } = useLocation();

  useEffect(() => {
    document.body.classList.toggle('cid-desktop', desktop);
    return () => document.body.classList.remove('cid-desktop');
  }, [desktop]);

  // The view in this tab is over: the ended page on every route (Back, a typed
  // "/"), never this browser's own sign-in. /view-as stays open so a new view
  // link can still be used in the same tab.
  if (viewAs.ended && pathname !== '/view-as') return <ViewAsPage ended />;

  if (desktop) {
    return (
      /* Learn Mode wraps the desktop tree exactly as it wraps the phone one: the
         lessons run ON these pages, the provider owns which step is current and
         puts the app into its fixture sandbox, and the overlay draws over the
         top. The lessons are shared — what changes between the builds is only
         which control each step points at and what it says about it, and that
         lives in src/learn/tourDesktop.js. */
      <TourProvider>
        <TourOverlay />
        <ReloadPrompt />
        {/* Release notes — the update lands in the background, so it is announced
            wherever the agent happens to be. Reopenable from the avatar menu. */}
        {/* HQ viewing as the agent: no release notes or memo gate to confirm —
            the view cannot write, and it is not the agent reading them. */}
        {!viewing && <WhatsNew />}
        {/* Memos from HQ that must be confirmed — over any screen, ahead of the release notes. */}
        {!viewing && <MemoGate />}
        {viewing && <ViewAsBanner />}
        <Routes>
          <Route path="/view-as" element={<ViewAsPage />} />
          <Route path="/view-as/ended" element={<ViewAsPage />} />
          <Route path="/login" element={<DesktopLogin />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Customer e-signing keeps ONE responsive layout: it is a token-gated
              page opened from an email on whatever device the customer has. */}
          <Route path="/sign/:token" element={<PublicSigningPage />} />
          {/* External guests register for an HQ event: public, one responsive layout too. */}
          <Route path="/join/:code" element={<OnDemand><JoinEventPage /></OnDemand>} />
          <Route path="*" element={<DesktopApp />} />
        </Routes>
      </TourProvider>
    );
  }

  return (
    <TourProvider>
    {/* The guided tour runs on top of the real pages — see learn/TourOverlay. */}
    <TourOverlay />
    {/* "New version available" banner — visible on every route (incl. public signing). */}
    <ReloadPrompt />
    {/* "Add to home screen" sheet — shows from the login screen onward so an agent
        can install BEFORE signing in; gates itself off the public-signing screens. */}
    {!viewing && <InstallPrompt />}
    {/* "Turn on notifications" sheet — the soft ask, just after sign-in. Waits for
        the install sheet, since on iOS push only works once the app is installed. */}
    {!viewing && <PushPrompt />}
    {/* "What’s new" — a short pop-up once after the app updates itself; the full page is on More. */}
    {!viewing && <WhatsNew />}
    {/* Memos from HQ that must be confirmed — a sheet over any screen until "I have read this". */}
    {!viewing && <MemoGate />}
    {/* HQ viewing as the agent from the admin portal — read only, this tab only. */}
    {viewing && <ViewAsBanner />}
    <Routes>
      <Route path="/view-as" element={<ViewAsPage />} />
      <Route path="/view-as/ended" element={<ViewAsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* PUBLIC customer e-signing — token-gated, no agent login (opened from the email link). */}
      <Route path="/sign/:token" element={<PublicSigningPage />} />
      {/* PUBLIC guest registration for an HQ event — no login (a link HQ shares, or an emailed invitation). */}
      <Route path="/join/:code" element={<OnDemand><JoinEventPage /></OnDemand>} />

      {/* tab group — bottom nav */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/listings" element={<ListingsPage />} />
        <Route path="/earnings" element={<EarningsPage />} />
        <Route path="/more" element={<MorePage />} />
      </Route>

      {/* full-screen group */}
      <Route element={<FullScreen />}>
        <Route path="/sales/new" element={<SaleCreatePage />} />
        <Route path="/sales/:id/edit" element={<SaleCreatePage />} />
        <Route path="/sales/:id" element={<SaleDetailPage />} />
        <Route path="/branch-sales/:id" element={<BranchSalePage />} />
        <Route path="/listings/new" element={<ListingFormPage />} />
        <Route path="/listings/:id/edit" element={<ListingFormPage />} />
        <Route path="/listings/:id" element={<ListingDetailPage />} />
        {/* Internal co-share — both inboxes, and one request end to end. */}
        <Route path="/co-share" element={<CoSharePage />} />
        <Route path="/co-share/:id" element={<CoShareDetailPage />} />
        {/* The fee-sharing agreement the RENs on a multi-agent sale sign. */}
        <Route path="/co-share-forms/:id" element={<CoShareFormPage />} />
        <Route path="/earnings/tier-ladder" element={<TierLadderPage />} />
        <Route path="/earnings/statement" element={<StatementPreviewPage />} />
        <Route path="/downloads" element={<DownloadsPage />} />
        {/* Release notes: every version, with demo clips. Reached from More. */}
        <Route path="/updates" element={<OnDemand><WhatsNewPage /></OnDemand>} />
        {/* Tutorials: how-it-works videos. Reached from More. */}
        <Route path="/tutorials" element={<OnDemand><TutorialsPage /></OnDemand>} />
        <Route path="/tutorials/:id" element={<OnDemand><TutorialWatchPage /></OnDemand>} />
        <Route path="/downloads/:id" element={<DocumentWorkspacePage />} />
        {/* Project materials — HQ's brochures, price lists, photos and videos per project. Reached from More. */}
        <Route path="/projects" element={<OnDemand><ProjectsPage /></OnDemand>} />
        <Route path="/projects/:id" element={<OnDemand><ProjectMaterialsPage /></OnDemand>} />
        <Route path="/forms" element={<FormsPage />} />
        <Route path="/forms/new" element={<FormCreatePage />} />
        <Route path="/forms/:id/edit" element={<FormCreatePage />} />
        <Route path="/forms/:id" element={<FormDetailPage />} />
        <Route path="/letters" element={<LettersPage />} />
        <Route path="/letters/new" element={<LetterCreatePage />} />
        <Route path="/letters/:id/edit" element={<LetterCreatePage />} />
        <Route path="/letters/:id" element={<LetterDetailPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/:type/new" element={<ContactFormPage />} />
        <Route path="/contacts/:type/:id/edit" element={<ContactFormPage />} />
        <Route path="/contacts/:type/:id" element={<ContactDetailPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/team/hierarchy" element={<TeamHierarchyPage />} />
        <Route path="/team/performance" element={<TeamPerformancePage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/tools/mortgage" element={<MortgagePage />} />
        <Route path="/tools/mortgage/result" element={<MortgageResultPage />} />
        <Route path="/tools/compare" element={<ComparePage />} />
        <Route path="/tools/refinance" element={<RefinancePage />} />
        <Route path="/tools/refinance/result" element={<RefinanceResultPage />} />
        <Route path="/tools/tenancy" element={<TenancyPage />} />
        <Route path="/tools/valuation" element={<ValuationPage />} />
        <Route path="/tools/lad" element={<LadPage />} />
        <Route path="/tools/affordability" element={<AffordabilityPage />} />
        <Route path="/tools/dsr" element={<DsrPage />} />
        <Route path="/sims" element={<SimsPage />} />
        <Route path="/notifs" element={<NotifsPage />} />
        <Route path="/memos" element={<MemosPage />} />
        <Route path="/memos/:id" element={<MemoDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Events — HQ's calendar, registration, tickets, self check-in. The fixed
            paths sit above /events/:id so "scan" and "checkin" are never read as ids. */}
        <Route path="/events" element={<OnDemand><EventsPage /></OnDemand>} />
        <Route path="/events/scan" element={<OnDemand><EventScanPage /></OnDemand>} />
        <Route path="/events/checkin" element={<OnDemand><EventCheckinLinkPage /></OnDemand>} />
        <Route path="/events/:id" element={<OnDemand><EventDetailPage /></OnDemand>} />
        <Route path="/events/:id/ticket" element={<OnDemand><EventTicketPage /></OnDemand>} />
        {/* Learn Mode — guided practice deals. Self-contained: no server calls at all. */}
        <Route path="/learn" element={<LearnPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </TourProvider>
  );
}
